import { useEffect, useRef } from 'react';
import type { Referral } from '../../api/types';
import { formatDate, formatMoney } from './referralDates';

interface Props {
  referral: Referral;

  /** The first payroll run on or after today, or null when the calendar has none left. */
  nextRun: string | null;
  saving: boolean;
  error: string | null;
  onAnswer: (paid: boolean) => void;
  onClose: () => void;
}

/**
 * The one question a missed payout raises: did the money go out or not?
 *
 * It is asked rather than assumed. Rolling the row forward on its own would erase the fact that a run
 * was missed, and would move rows that *were* paid on time and merely never ticked — putting a date
 * in the record that nothing happened on. Both answers here close the referral out completely, so
 * nothing is left half-resolved.
 */
export function MissedPayoutDialog({ referral, nextRun, saving, error, onAnswer, onClose }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  return (
    <dialog className="dialog dialog--narrow" ref={dialogRef} onCancel={onClose} onClose={onClose}>
      <div className="dialog__form">
        <h2 className="dialog__title">This payout was missed</h2>

        {error !== null && <p className="dialog__error">{error}</p>}

        <div className="dialog__body">
          <p>
            The {formatMoney(referral.payoutAmount)} bonus for{' '}
            <strong>{referral.newHireName}</strong>, referred by {referral.referringEmployeeName}, was
            due on <strong>{formatDate(referral.payoutDate)}</strong> — and nobody confirmed it before
            that run went out.
          </p>
          <p>Which is it?</p>
        </div>

        <div className="dialog__choices">
          <button
            type="button"
            className="choice"
            disabled={saving}
            onClick={() => onAnswer(true)}
          >
            <span className="choice__title">It was paid</span>
            <span className="choice__detail">
              The money went out on {formatDate(referral.payoutDate)} and only the tick was forgotten.
              Marks it eligible, paid and done.
            </span>
          </button>

          <button
            type="button"
            className="choice"
            disabled={saving || nextRun === null}
            onClick={() => onAnswer(false)}
          >
            <span className="choice__title">
              Not paid — move it to {nextRun === null ? 'the next run' : formatDate(nextRun)}
            </span>
            <span className="choice__detail">
              {nextRun === null
                ? 'There is no payroll run left in the calendar. Add one on the Payout dates page first.'
                : 'Still owed. Marks it eligible and holds it to that run instead of a date that has gone.'}
            </span>
          </button>
        </div>

        <div className="dialog__actions">
          <button type="button" className="button button--quiet" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
}
